/**
 * Atlas date and time formatting. One implementation, so a surface cannot end up with six.
 *
 * The contract is `packages/ui/design/conventions.md` §1. The short version:
 *
 *   `16 Sep '26`                       an absolute date
 *   `16 Sep '26 · 14:30`               with a time, 24-hour
 *   `19 Sep → 21 Sep '26 · 2 days`     a range, arrow not en-dash, duration stated
 *   `4 h ago` / `in 36 h`              inside seven days
 *
 * WHY THIS EXISTS. The customer drawer rendered six formats at once — `16 d ago`, `8/31/2026`,
 * `Sep 11, 2026`, `31 Aug`, `12:10:15 PM`, `9/17/2026, 11:11:24 AM` — because most call sites used
 * a bare `toLocaleDateString()`. That follows the VIEWER's browser, so `9/16/2026` renders for one
 * agent and `16/9/2026` for another: the same string meaning two different days, with nothing on
 * screen to say which. Every function here pins the locale and the timezone.
 *
 * TIMEZONE. Everything is Dubai wall-clock. eZhire operates there, the shift roster is there, and
 * an agent in Lahore reading a Dubai booking needs the Dubai time or the number is wrong by four
 * hours. A surface showing times says so once, quietly — see `ATLAS_TZ_LABEL`.
 */

const TZ = 'Asia/Dubai';
const LOCALE = 'en-GB';

/**
 * Month names, fixed, three letters.
 *
 * NOT from Intl's `month: 'short'`. `en-GB` renders September as **"Sept"** — four letters — and
 * `en-US` renders it "Sep", so the width of one month depended on which locale tag was passed.
 * A four-letter month in a column of dates breaks the alignment the tabular numerals exist to give,
 * and ICU has changed these strings between Node versions before. The date format is a house
 * decision, so the strings are a house constant.
 */
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] as const;

/** Say this once per surface that shows times, in muted caption type. */
export const ATLAS_TZ_LABEL = 'Times are Dubai (GST)';

/** Ordinal suffix, exported for the one place that may want it. See `formatAtlasDate`. */
export function ordinalSuffix(day: number): string {
  if (day % 100 >= 11 && day % 100 <= 13) return 'th';
  switch (day % 10) {
    case 1: return 'st';
    case 2: return 'nd';
    case 3: return 'rd';
    default: return 'th';
  }
}

function parse(v: string | number | Date | null | undefined): Date | null {
  if (v === null || v === undefined || v === '') return null;
  const d = v instanceof Date ? v : new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** The Dubai-local calendar parts of an instant. */
function parts(d: Date): { day: number; month: string; year: string; hour: string; minute: string } {
  const fmt = new Intl.DateTimeFormat(LOCALE, {
    timeZone: TZ, day: 'numeric', month: 'numeric', year: '2-digit',
    hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
  });
  const out: Record<string, string> = {};
  for (const p of fmt.formatToParts(d)) if (p.type !== 'literal') out[p.type] = p.value;
  return {
    day: Number(out.day), month: MONTHS[Number(out.month) - 1] ?? '', year: out.year ?? '',
    hour: out.hour ?? '00', minute: out.minute ?? '00',
  };
}

export interface AtlasDateOptions {
  /** Drop the year. Only when a sibling value on the same surface carries it. */
  omitYear?: boolean;
  /**
   * Render `16th` instead of `16`.
   *
   * OFF by default, deliberately. At 12px the suffix is an ~8px glyph — visible but not legible —
   * it carries no information the numeral has not already given, and `1st`/`22nd`/`3rd` have
   * different widths, so a column of dates stops lining up. Consumers that want it must pass it,
   * and should render the suffix with `.ordinal` from the stylesheet rather than raw superscript.
   */
  ordinal?: boolean;
}

/** `16 Sep '26` — the one absolute date format. */
export function formatAtlasDate(v: string | number | Date | null | undefined, o: AtlasDateOptions = {}): string {
  const d = parse(v);
  if (!d) return '—';
  const p = parts(d);
  const day = o.ordinal ? `${p.day}${ordinalSuffix(p.day)}` : String(p.day);
  return o.omitYear ? `${day} ${p.month}` : `${day} ${p.month} '${p.year}`;
}

/** `16 Sep '26 · 14:30` — 24-hour, because Dubai operations run around the clock. */
export function formatAtlasDateTime(v: string | number | Date | null | undefined, o: AtlasDateOptions = {}): string {
  const d = parse(v);
  if (!d) return '—';
  const p = parts(d);
  return `${formatAtlasDate(d, o)} · ${p.hour}:${p.minute}`;
}

/** `14:30` on its own. */
export function formatAtlasTime(v: string | number | Date | null | undefined): string {
  const d = parse(v);
  if (!d) return '—';
  const p = parts(d);
  return `${p.hour}:${p.minute}`;
}

/**
 * Whole days between two instants, by DUBAI calendar day.
 *
 * A pure calendar difference: two instants inside one Dubai day are 0 apart, however many hours
 * separate them. `formatAtlasRange` floors the DISPLAY at one day; that is presentation, and it
 * deliberately does not live here.
 */
export function atlasDaysBetween(from: Date, to: Date): number {
  const key = (d: Date) => new Intl.DateTimeFormat('en-CA', { timeZone: TZ, year: 'numeric', month: '2-digit', day: '2-digit' }).format(d);
  const a = Date.parse(`${key(from)}T00:00:00Z`);
  const b = Date.parse(`${key(to)}T00:00:00Z`);
  return Math.max(0, Math.round((b - a) / 86_400_000));
}

/**
 * `19 Sep → 21 Sep '26 · 2 days`
 *
 * An ARROW, not an en-dash: at 12px a dash between two dates reads as a hyphenated single date.
 * The duration is stated because otherwise every reader subtracts it themselves, and a rental's
 * length is the thing they are usually actually after. The year appears once when both ends share
 * it, and on BOTH ends when they do not — `28 Dec '25 → 4 Jan '26` has to be unambiguous.
 */
export function formatAtlasRange(
  from: string | number | Date | null | undefined,
  to: string | number | Date | null | undefined,
  o: { withDuration?: boolean } = {},
): string {
  const a = parse(from);
  const b = parse(to);
  if (!a && !b) return '—';
  if (!a || !b) return `${a ? formatAtlasDate(a) : '—'} → ${b ? formatAtlasDate(b) : '—'}`;
  const sameYear = parts(a).year === parts(b).year;
  const head = formatAtlasDate(a, { omitYear: sameYear });
  const tail = formatAtlasDate(b);
  const span = `${head} → ${tail}`;
  if (o.withDuration === false) return span;
  /*
   * Floored at one day for display. A rental that starts and ends on the same Dubai calendar day
   * is a real booking priced as one day; rendering "0 days" beside it reads as a data fault and an
   * agent would query it on a call. `atlasDaysBetween` itself stays a pure calendar difference —
   * the floor is a presentation decision and belongs here, not in the arithmetic.
   */
  const days = Math.max(1, atlasDaysBetween(a, b));
  return `${span} · ${days} ${days === 1 ? 'day' : 'days'}`;
}

/**
 * `4 h ago` / `in 36 h` inside seven days; the absolute date beyond it.
 *
 * Past seven days a relative time stops being useful — `16 d ago` is already harder to act on than
 * `1 Sep '26`, and by `94 d ago` it is noise. The cutoff is the rule, not a preference.
 */
export function formatAtlasRelative(v: string | number | Date | null | undefined, now: Date = new Date()): string {
  const d = parse(v);
  if (!d) return '—';
  const ms = d.getTime() - now.getTime();
  const abs = Math.abs(ms);
  const future = ms > 0;

  if (abs < 60_000) return 'just now';
  if (abs > 7 * 86_400_000) return formatAtlasDate(d);

  const mins = Math.round(abs / 60_000);
  const unit = mins < 60 ? `${mins} min`
    : abs < 2 * 86_400_000 ? `${Math.round(abs / 3_600_000)} h`
    : `${Math.round(abs / 86_400_000)} d`;
  return future ? `in ${unit}` : `${unit} ago`;
}

/**
 * The absolute value for a `title` attribute beside a relative one.
 *
 * A relative time must never be the ONLY form available — conventions.md §1. This is the cheapest
 * way to satisfy that on a row too tight to show both.
 */
export function atlasExactTitle(v: string | number | Date | null | undefined): string | undefined {
  const d = parse(v);
  return d ? `${formatAtlasDateTime(d)} (Dubai)` : undefined;
}
